/**
 * What these plugins report about themselves, and what they deliberately don't.
 *
 * Two endpoints, both Cloudflare Pages Functions writing to one D1 database:
 * `/api/track/usage` for what the plugin is being used for, `/api/track/error`
 * for what went wrong. See `functions/api/track/`.
 *
 * The line this stays on: **a document never leaves the machine it was opened
 * on**. Not its bytes, not its name, not its artboard names, not its fonts, not
 * its dimensions. That's the plugins' whole pitch and it survives this intact —
 * but only because the payloads below have nowhere to put any of it.
 * `trackUsage` takes a fixed set of fields and the error reporter takes what the
 * shared logger already built; neither accepts a free-form bag that a later
 * caller could quietly drop a file name into.
 *
 * Every row carries which of the two plugins sent it. The UI learns that from
 * the sandbox handshake, so nothing is reported before one arrives — and since
 * the handshake is also what unlocks the rest of the UI, there is nothing worth
 * reporting before then anyway.
 *
 * The paths are relative, unlike `photoshop-to-figma`'s, which needs an absolute
 * URL because its UI is served from `__html__` and has no origin. Here the UI is
 * served by the very project that hosts the endpoints, so a relative path is
 * both correct and self-adjusting: a preview deploy reports to its own database
 * rather than production's, with nothing to configure.
 *
 * Everything here fails quietly. A blocked request, an offline machine, a
 * project deployed without a database — none of those are a designer's problem
 * mid-import, and none of them may surface. The one thing that would be worse
 * than losing a metric is an error dialog about losing a metric.
 */

import { setErrorReporter, type ErrorRecord } from '@figma-importers/ui/logger';

/** The events the server will accept. Kept in step with `functions/api/track/usage.ts`. */
export type UsageEvent =
  | 'session'
  | 'import-start'
  | 'import-complete'
  | 'import-cancelled'
  | 'import-failed';

export interface UsageFields {
  editor?: string | null;
  /** 'vector' or 'image' — how the artwork was brought in, never what it was. */
  mode?: string | null;
  /** How many artboards the import produced — a count, never a name. */
  layers?: number | null;
  /** How long it took, milliseconds. */
  ms?: number | null;
}

/**
 * Who and what to attribute a row to: the plugin, Figma's pseudonymous user id,
 * and nothing derived from either.
 */
let identity: { app: string | null; userId: string | null; userName: string | null } = {
  app: null,
  userId: null,
  userName: null,
};

let enabled = true;

/**
 * Stop reporting for the rest of this session.
 *
 * Called when a send fails in a way that says the destination isn't there — a
 * plugin running against a deploy without the endpoints shouldn't spend the rest
 * of its life retrying, and a designer working offline shouldn't have a failed
 * request logged for every artboard that goes wrong.
 */
function stop(): void {
  enabled = false;
}

/**
 * Fire and forget.
 *
 * `text/plain` on purpose: `application/json` is not a CORS-safelisted content
 * type, so it costs a preflight round trip before every event. The endpoint
 * reads the body as JSON regardless of what the header claims, so the header
 * buys nothing and costs a request.
 *
 * `keepalive` so a report sent as the plugin closes still goes.
 */
function send(path: string, payload: Record<string, unknown>): void {
  // Nothing is attributable before the handshake, and the server refuses a row
  // that can't say which plugin it came from — so don't spend the request.
  if (!enabled || !identity.app) return;
  try {
    void fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body: JSON.stringify({ ...payload, app: identity.app }),
      keepalive: true,
    }).catch(stop);
  } catch {
    // `fetch` itself throwing — a manifest that doesn't allow the domain, a
    // realm without one at all — means every later call would throw too.
    stop();
  }
}

/**
 * Attribute later events to a plugin and to whoever Figma says is signed in.
 * Called from the handshake, which is the first moment either is known.
 */
export function identify(
  app: string,
  user: { id: string | null; name: string } | null,
): void {
  identity = { app, userId: user?.id ?? null, userName: user?.name ?? null };
}

export function trackUsage(event: UsageEvent, fields: UsageFields = {}): void {
  send('/api/track/usage', {
    event,
    userId: identity.userId,
    // Only with the session row, which is the only place the server stores it.
    userName: event === 'session' ? identity.userName : undefined,
    editor: fields.editor ?? undefined,
    mode: fields.mode ?? undefined,
    layers: fields.layers ?? undefined,
    ms: fields.ms ?? undefined,
    build: __BUILD_ID__,
    ts: Date.now(),
  });
}

/**
 * How much of a stack is worth sending.
 *
 * Past the server's body cap a report is refused outright rather than
 * truncated, and a runaway recursion produces a stack far past it. The frames
 * that identify the fault are the first ones, so the prefix is what's kept: a
 * report that arrives trimmed beats one that never arrives.
 */
const STACK_LIMIT = 4000;

/**
 * At most this many error reports per session, and never the same one twice.
 *
 * A failure inside a render or a per-artboard loop doesn't happen once. Without
 * this, the first import to hit one turns into a few hundred identical POSTs,
 * which is both a useless table and something that looks a lot like an attack on
 * your own endpoint. Deduplication is by message, so a loop reports once.
 */
const REPORT_LIMIT = 20;
const seen = new Set<string>();

function reportError(record: ErrorRecord): void {
  if (seen.size >= REPORT_LIMIT || seen.has(record.message)) return;
  seen.add(record.message);
  send('/api/track/error', {
    name: record.name,
    message: record.message,
    stack: record.stack?.slice(0, STACK_LIMIT),
    phase: record.phase,
    userId: identity.userId,
    build: __BUILD_ID__,
    ts: Date.parse(record.timestamp) || Date.now(),
  });
}

/**
 * Start reporting. Wires the shared logger's sink, which is where every error in
 * the UI already flows — including uncaught ones and unhandled rejections, via
 * `installGlobalErrorHandlers`.
 */
export function initTelemetry(): void {
  setErrorReporter(reportError);
}

/** Testing seam: forget what this session has already reported. */
export function resetTelemetry(): void {
  enabled = true;
  seen.clear();
  identity = { app: null, userId: null, userName: null };
}
